import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Receipt,
  Settings,
  LogOut,
  Bell,
  ChevronDown,
  ClipboardCheck,
  Wrench,
  Wallet,
  MessageSquare,
  ScrollText,
  TrendingUp,
  BellRing,
  CreditCard,
  BookOpen,
  Menu,
  MoreHorizontal,
  Lock,
  Eye,
  Coins,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Toaster } from "@/components/ui/sonner";
import { LogoMark } from "@/components/Logo";
import { planContext } from "@/lib/plan";
import { ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/app", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/app/biens", label: "Biens", icon: Building2, testid: "nav-properties" },
  { to: "/app/locataires", label: "Locataires", icon: Users, testid: "nav-tenants" },
  { to: "/app/baux", label: "Baux", icon: FileText, testid: "nav-leases" },
  { to: "/app/quittances", label: "Quittances", icon: Receipt, testid: "nav-receipts" },
  { to: "/app/etats-des-lieux", label: "États des lieux", icon: ClipboardCheck, testid: "nav-inspections", demoLocked: true },
  { to: "/app/incidents", label: "Incidents", icon: Wrench, testid: "nav-incidents", demoLocked: true },
  { to: "/app/finances", label: "Finances", icon: Wallet, testid: "nav-finances" },
  { to: "/app/alertes", label: "Alertes", icon: BellRing, testid: "nav-alerts", demoLocked: true },
  { to: "/app/revision-irl", label: "Révision IRL", icon: TrendingUp, testid: "nav-irl", demoLocked: true },
  { to: "/app/courriers", label: "Courriers", icon: ScrollText, testid: "nav-letters", demoLocked: true },
  { to: "/app/assistant", label: "Assistant IA", icon: MessageSquare, testid: "nav-assistant", demoLocked: true },
  { to: "/app/ressources", label: "Ressources", icon: BookOpen, testid: "nav-resources" },
  { to: "/app/abonnement", label: "Abonnement", icon: CreditCard, testid: "nav-subscription" },
  { to: "/app/parametres", label: "Paramètres", icon: Settings, testid: "nav-settings" },
];

// 4 raccourcis principaux pour la barre du bas (mobile). Le 5e bouton = "Plus"
// qui ouvre le tiroir de navigation complet.
const MOBILE_PRIMARY = NAV.slice(0, 4);

function NavItem({ n, locked, onClick }) {
  return (
    <NavLink
      to={n.to}
      end={n.end}
      data-testid={n.testid}
      onClick={onClick}
      className={({ isActive }) =>
        `side-link ${isActive ? "active" : ""} flex items-center gap-2`
      }
    >
      <n.icon className="h-4 w-4" />
      <span className="flex-1">{n.label}</span>
      {locked && <Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { active, isAdmin } = planContext(user);
  const navItems = user?.is_super_admin
    ? [...NAV, { to: "/app/admin", label: "Gestion du site", icon: ShieldCheck, testid: "nav-admin" }]
    : NAV;

  // Blocage total : sans abonnement actif (et hors admin), l'utilisateur est
  // redirigé vers la page Abonnement. C'est la seule page accessible tant
  // qu'aucune formule n'est activée.
  const blocked = !isAdmin && !active;
  // La page Abonnement reste accessible pour souscrire ; Paramètres reste
  // accessible pour la gestion/suppression de compte (conformité RGPD).
  const ALLOWED_WHEN_BLOCKED = ["/app/abonnement", "/app/parametres"];
  if (blocked && !ALLOWED_WHEN_BLOCKED.includes(location.pathname)) {
    return <Navigate to="/app/abonnement" replace />;
  }

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex min-h-screen bg-[hsl(var(--background))]">
      {/* Sidebar desktop */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex md:w-64 md:flex-col border-r border-slate-200 bg-white"
      >
        <div className="flex h-16 items-center gap-2 px-6 border-b border-slate-200">
          <LogoMark className="h-8" />
          <div className="font-heading font-bold text-slate-900 tracking-tight">
            Gestimmony
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((n) => (
            <NavItem key={n.to} n={n} locked={n.demoLocked && user?.is_demo} />
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 text-xs text-slate-400">
          v1.0 — Gestion locative
        </div>
      </aside>

      {/* Tiroir de navigation mobile (menu complet) */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-white flex flex-col" data-testid="mobile-nav-drawer">
          <div className="flex h-16 items-center gap-2 px-6 border-b border-slate-200 shrink-0">
            <LogoMark className="h-8" />
            <SheetTitle className="font-heading font-bold text-slate-900 tracking-tight">
              Gestimmony
            </SheetTitle>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((n) => (
              <NavItem key={n.to} n={n} locked={n.demoLocked && user?.is_demo} onClick={() => setMenuOpen(false)} />
            ))}
          </nav>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          data-testid="app-header"
          className="h-16 shrink-0 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 sticky top-0 z-30"
        >
          <div className="flex items-center gap-2 md:hidden">
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              className="text-slate-600 -ml-2"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <span className="font-heading font-bold text-slate-900">Gestimmony</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500">
            Bonjour <span className="font-medium text-slate-900">{user?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" data-testid="header-notifications" className="text-slate-500">
              <Bell className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-user-menu"
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100 transition-colors"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[hsl(var(--primary))] text-white text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm text-slate-700 max-w-[140px] truncate">
                    {(user?.name || user?.email || "").split(" ")[0]}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Barre du bas mobile : 4 raccourcis + bouton "Plus" (ouvre le tiroir) */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex justify-around py-2">
          {MOBILE_PRIMARY.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${
                  isActive ? "text-[hsl(var(--primary))]" : "text-slate-500"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label.split(" ")[0]}
            </NavLink>
          ))}
          <button
            type="button"
            data-testid="mobile-more-button"
            onClick={() => setMenuOpen(true)}
            className="flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] text-slate-500"
          >
            <MoreHorizontal className="h-4 w-4" />
            Plus
          </button>
        </nav>

        {user?.is_demo && (
          <div
            data-testid="demo-banner"
            className="bg-[hsl(var(--primary))] text-white px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 text-sm"
          >
            <span className="flex items-center gap-2 min-w-0">
              <Eye className="h-4 w-4 shrink-0" />
              <span className="truncate">
                <b>Mode démo</b> — vous explorez Gestimmony en lecture seule avec des données d'exemple.
              </span>
            </span>
            <button
              data-testid="demo-banner-signup"
              onClick={async () => { await logout(); navigate("/register"); }}
              className="shrink-0 rounded-full bg-white text-[hsl(var(--primary))] font-semibold text-xs px-3 py-1.5 hover:bg-stone-100 transition-colors"
            >
              Créer mon compte
            </button>
          </div>
        )}

        {user?.is_member && (
          <div
            data-testid="member-banner"
            className="bg-teal-600 text-white px-4 md:px-8 py-2.5 flex items-center gap-2 text-sm"
          >
            <Eye className="h-4 w-4 shrink-0" />
            <span className="truncate">
              <b>Accès collaborateur</b> — vous consultez ce compte en lecture seule. La création et la
              modification sont réservées au titulaire.
            </span>
          </div>
        )}

        {blocked && (
          <div
            data-testid="readonly-banner"
            className="bg-amber-50 border-b border-amber-200 px-4 md:px-8 py-2.5 flex items-center gap-2 text-sm text-amber-900"
          >
            <Lock className="h-4 w-4 shrink-0" />
            <span>
              <b>Abonnement requis</b> — choisissez une formule ci-dessous pour accéder à votre
              espace (biens, baux, quittances…).
            </span>
          </div>
        )}

        <main className="flex-1 p-4 md:p-8 pb-20 md:pb-8 min-w-0">
          <Outlet />
        </main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
}
